<script setup lang="ts">
import { ref, toRefs, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    count: number | string
    start: boolean
    speed?: 'fast' | 'medium' | 'slow'
    baseClass?: string
    activeClass?: string
  }>(),
  {
    speed: 'medium',
    baseClass: 'text-black text-s',
    activeClass: 'text-white',
  }
)

const { count, start, speed, baseClass, activeClass } = toRefs(props)

const emits = defineEmits<{
  (e: 'update', isUpdating: boolean): boolean
  (e: 'finished'): void
}>()

const currentCount = ref(count.value)
const updatedCount = ref()
const isUpdating = ref()

const speedConfig = {
  fast: {
    duration: 'duration-300',
    delay: 'delay-150',
    timeout: 400,
  },
  medium: {
    duration: 'duration-500',
    delay: 'delay-300',
    timeout: 800,
  },
  slow: {
    duration: 'duration-1000',
    delay: 'delay-500',
    timeout: 1200,
  },
}

const changeUpdateStatus = (value: boolean) => {
  emits('update', value)

  isUpdating.value = value
}

const finishTransition = () => {
  emits('finished')
}

watch(count, () => {
  if (count.value === currentCount.value) {
    finishTransition()

    return
  }

  updatedCount.value = count.value

  changeUpdateStatus(true)

  setTimeout(() => {
    currentCount.value = updatedCount.value

    changeUpdateStatus(false)

    finishTransition()
  }, speedConfig[speed.value].timeout)
})
</script>

<template>
  <div class="h-full overflow-hidden">
    <div
      class="h-[300%] -translate-y-1/3 text-center font-sans"
      :class="{
        '!-translate-y-2/3': currentCount > count,
        'translate-y-0': currentCount < count,
        [`${speedConfig[speed].duration} ${speedConfig[speed].delay} linear`]:
          isUpdating,
      }"
    >
      <div
        class="flex h-1/3 items-center justify-center transition-all"
        :class="[isUpdating && baseClass, activeClass]"
      >
        {{ updatedCount }}
      </div>
      <div
        class="flex h-1/3 items-center justify-center transition-all"
        :class="[start ? activeClass : baseClass]"
      >
        {{ currentCount }}
      </div>
      <div
        class="flex h-1/3 items-center justify-center transition-all"
        :class="[isUpdating && baseClass, activeClass]"
      >
        {{ updatedCount }}
      </div>
    </div>
  </div>
</template>
